import React, { useMemo } from "react";
import MirsTable from "components/general/table/Table";
import { useViewport } from 'components/general/useViewPort'
import {
    isLiveStatus,
    isStartListStatus,
    isFinishedStatus
} from "helpers/status";
import CaptionTable from "components/general/table/CaptionTable";
import Status from "components/presentation/Status";
import {
    TableColumnsStartList as ColumnsStartList,
    TableColumnsLive as ColumnsLive,
    TableColumnsStartListSM as ColumnsStartListSM,
    TableColumnsLiveSM as ColumnsLiveSM,
    TableColumnsStartListFNL as ColumnsStartListFNL,
    TableColumnsLiveFNL as ColumnsLiveFNL,
    TableColumnsStartListFNL_SM as ColumnsStartListFNL_SM,
    TableColumnsLiveFNL_SM as ColumnsLiveFNL_SM
} from 'components/sports/gtr/components/TableColumns'
import LoaderSubComponent from "components/general/LoaderSubComponent";
import TableExpanded from "components/sports/gtr/views/TableExpanded";
import TableExpandedFNL from "components/sports/gtr/views/TableExpandedFNL";
import CurrentCompetitor from "components/sports/gtr/views/CurrentCompetitor";
import PreviousCompetitor from "components/sports/gtr/views/PreviousCompetitor";
import { findArray } from 'helpers/utils'
import Label from "components/general/Label";
import { getPhase } from "helpers/rsc";


const getConfiguration = (data, resultStatus, breakPoint, rsc, showQualMark, routine) => {

    let configuration = {
        caption: null,
        columns: [],
        hiddenColumns: [],
        sortBy: [],
    };

    const scheduleStatus = data?.results?.schedule?.status?.code;
    let showESL = data?.results?.items?.some(item => item.extCompetitor);

    //HiddenColumns
    if (!showQualMark)
        configuration.hiddenColumns.push('QualMark')
    if (isFinishedStatus(resultStatus))
        configuration.hiddenColumns.push('StartOrder')
    if (!showESL && isStartListStatus(resultStatus))
        configuration.hiddenColumns.push('Expander')

    //Caption
    configuration.caption = <CaptionTable
        leftCol={isStartListStatus(resultStatus) ? <Label data={'HeaderStartList'} /> : <Label data={'HeaderResults'} />}
        rightCol={<Status typeToPrint="result" scheduleStatus={scheduleStatus} resultStatus={resultStatus} />}
    />

    //Sort
    configuration.sortBy = isStartListStatus(resultStatus)
        ? [{ id: 'StartOrder', desc: false }]
        : [{ id: 'Rank', desc: false }]

    //Columns
    if (getPhase(rsc).includes('FNL')) {
        if (isStartListStatus(resultStatus)) {
            if (breakPoint === 'xs' || breakPoint === 'sm') {
                configuration.columns = ColumnsStartListFNL_SM()
            }
            else {
                configuration.columns = ColumnsStartListFNL()
            }
        }

        if (isLiveStatus(resultStatus) || isFinishedStatus(resultStatus)) {
            if (breakPoint === 'xs' || breakPoint === 'sm') {
                configuration.columns = ColumnsLiveFNL_SM()
            } else {
                configuration.columns = ColumnsLiveFNL()
            }
        }
    }
    else {
        if (isStartListStatus(resultStatus)) {
            if (breakPoint === 'xs' || breakPoint === 'sm') {
                configuration.columns = ColumnsStartListSM()
            }
            else {
                configuration.columns = ColumnsStartList(routine)
            }
        }

        if (isLiveStatus(resultStatus) || isFinishedStatus(resultStatus)) {
            if (breakPoint === 'xs' || breakPoint === 'sm') {
                configuration.columns = ColumnsLiveSM()
            } else {
                configuration.columns = ColumnsLive(routine)
            }
        }
    }
    return configuration
}


const GtrResults = ({ data }) => {
    const { breakPoint } = useViewport();
    let resultStatus = data?.results?.status?.code;
    const rsc = data?.results?.eventUnitCode;
    let showQualMark = data?.results?.items?.some(item => item.qualificationMark);
    let routine = findArray(data?.results?.extendedInfos, { ei_code: ['ROUTINE'] }).ei_value

    const config = useMemo(() => getConfiguration(data, resultStatus, breakPoint, rsc, showQualMark, routine), [resultStatus, breakPoint, data]);

    const renderRowSubComponent = React.useCallback(({ row }) => (
        <LoaderSubComponent component={
            getPhase(rsc).includes('FNL')
                ? <TableExpandedFNL row={row?.original} status={resultStatus} />
                : <TableExpanded row={row?.original} showQualMark={showQualMark}/>

        }
        />
    ), [resultStatus, showQualMark, data]);

    //Gets current competitor
    let currentCompetitor = data?.results?.items?.find(c =>
        c?.extendedResults?.find(er => er?.er_code === 'STAGE' && (er?.er_value === 'CURRENT' || er?.er_value === 'WAITING'))
    )
    //Gets previous competitor
    let previousCompetitor = data?.results?.items?.find(c =>
        c?.extendedResults?.find(er => er?.er_code === 'STAGE' && er?.er_value === 'LAST')
    ) 

    return (
        <>
            {data?.results?.items
                ? (
                    <>
                        {!isFinishedStatus(resultStatus) && <>
                            <PreviousCompetitor competitor={previousCompetitor} routine={routine} rsc={rsc}/>
                            <CurrentCompetitor competitor={currentCompetitor} routine={routine} />
                        </>}

                        <MirsTable
                            //Dato to show by row
                            data={data?.results?.items}
                            //Columnas a visualizar 
                            columns={config.columns}
                            caption={config.caption}
                            keyRow='participant-code'
                            renderRowSubComponent={renderRowSubComponent}
                            hiddenColumns={config.hiddenColumns}
                            initialSortBy={config.sortBy}
                            getRowProps={row => ({
                                className: currentCompetitor?.participant?.code?.includes(row?.original?.participant?.code) && !isFinishedStatus(resultStatus)
                                    ? 'currentIndicator'
                                    : '',
                            })}
                            id={`mirs-table-result-${rsc}`}
                        />
                    </>
                ) : (
                    ''
                )
            }
        </>
    );
}

export default GtrResults;